/**
 * @fileoverview Full Notification Inbox Modal
 *
 * Modal-based full inbox view with filters, bulk actions, and notification management.
 * Uses React Portal to render at document body level for proper z-index stacking.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    FiBell,
    FiX,
    FiCheck,
    FiCheckCircle,
    FiAlertCircle,
    FiInfo,
    FiAlertTriangle,
    FiTrash2,
    FiClock,
    FiExternalLink,
    FiInbox
} from 'react-icons/fi';
import { useNotificationCenter } from '../context/NotificationCenterContext';
import '../styles/NotificationInboxFull.css';

const NotificationInboxFull = ({ isOpen, onClose, onNavigate }) => {
    const {
        notifications,
        isLoading,
        fetchNotifications,
        markAsRead,
        dismissNotification,
        markAllAsRead,
        dismissAll
    } = useNotificationCenter();

    const [filter, setFilter] = useState('all'); // all, unread, read
    const [categoryFilter, setCategoryFilter] = useState('all'); // all, time_bank, invoice, system

    // Fetch notifications when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, fetchNotifications]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Filter notifications
    const filteredNotifications = notifications.filter(notification => {
        // Filter by read status
        if (filter === 'unread' && notification.is_read) return false;
        if (filter === 'read' && !notification.is_read) return false;

        // Filter by category
        if (categoryFilter !== 'all' && notification.category !== categoryFilter) return false;

        return true;
    });

    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Get icon based on notification type
    const getNotificationIcon = (notification) => {
        const { type } = notification;

        switch (type) {
            case 'success':
                return <FiCheckCircle className="notif-icon-full success" />;
            case 'warning':
                return <FiAlertTriangle className="notif-icon-full warning" />;
            case 'error':
                return <FiAlertCircle className="notif-icon-full error" />;
            default:
                return <FiInfo className="notif-icon-full info" />;
        }
    };

    // Format relative time
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Handle notification click
    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        if (notification.action_url && onNavigate) {
            onNavigate(notification.action_url);
            onClose();
        }
    };

    // Handle dismiss all with confirmation
    const handleDismissAll = () => {
        if (window.confirm('Are you sure you want to dismiss all notifications?')) {
            dismissAll();
        }
    };

    // Use portal to render at document body level for proper z-index stacking
    return ReactDOM.createPortal(
        <div className="notification-inbox-full-overlay" onClick={onClose}>
            <div className="notification-inbox-full-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="nif-header">
                    <div className="nif-header-left">
                        <FiInbox className="nif-header-icon" />
                        <div>
                            <h2>Notifications</h2>
                            <span className="nif-subtitle">
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </span>
                        </div>
                    </div>
                    <button className="nif-close-btn" onClick={onClose}>
                        <FiX size={20} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="nif-toolbar">
                    <div className="nif-filters">
                        <div className="nif-filter-group">
                            <button
                                className={`nif-filter-btn ${filter === 'all' ? 'active' : ''}`}
                                onClick={() => setFilter('all')}
                            >
                                All
                            </button>
                            <button
                                className={`nif-filter-btn ${filter === 'unread' ? 'active' : ''}`}
                                onClick={() => setFilter('unread')}
                            >
                                Unread
                            </button>
                            <button
                                className={`nif-filter-btn ${filter === 'read' ? 'active' : ''}`}
                                onClick={() => setFilter('read')}
                            >
                                Read
                            </button>
                        </div>
                        <div className="nif-filter-divider"></div>
                        <select
                            className="nif-source-select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            <option value="time_bank">Time Bank</option>
                            <option value="invoice">Invoices</option>
                            <option value="system">System</option>
                        </select>
                    </div>
                    <div className="nif-actions">
                        {unreadCount > 0 && (
                            <button className="nif-action-btn" onClick={() => markAllAsRead()}>
                                <FiCheck size={16} />
                                Mark all read
                            </button>
                        )}
                        {notifications.length > 0 && (
                            <button className="nif-action-btn danger" onClick={handleDismissAll}>
                                <FiTrash2 size={16} />
                                Clear all
                            </button>
                        )}
                    </div>
                </div>

                {/* Notification List */}
                <div className="nif-list">
                    {isLoading ? (
                        <div className="nif-loading">
                            <div className="nif-spinner"></div>
                            <span>Loading notifications...</span>
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="nif-empty">
                            <FiBell className="nif-empty-icon" size={64} />
                            <h3>No notifications</h3>
                            <p>
                                {filter === 'unread'
                                    ? "You've read all your notifications"
                                    : filter === 'read'
                                    ? "No read notifications"
                                    : "You're all caught up!"}
                            </p>
                        </div>
                    ) : (
                        filteredNotifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`nif-item ${!notification.is_read ? 'unread' : ''}`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="nif-item-icon">
                                    {getNotificationIcon(notification)}
                                </div>
                                <div className="nif-item-content">
                                    <div className="nif-item-header">
                                        <span className="nif-item-title">{notification.title}</span>
                                        <div className="nif-item-badges">
                                            {notification.category && (
                                                <span className={`nif-category-badge ${notification.category}`}>
                                                    {notification.category.replace('_', ' ')}
                                                </span>
                                            )}
                                            {!notification.is_read && (
                                                <span className="nif-unread-badge">New</span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="nif-item-message">{notification.message}</p>
                                    <div className="nif-item-footer">
                                        <span className="nif-item-time">
                                            <FiClock size={12} />
                                            {formatRelativeTime(notification.created_at)}
                                        </span>
                                        {notification.action_text && (
                                            <button className="nif-item-action">
                                                {notification.action_text}
                                                <FiExternalLink size={12} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="nif-item-actions">
                                    {!notification.is_read && (
                                        <button
                                            className="nif-item-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsRead(notification.id);
                                            }}
                                            title="Mark as read"
                                        >
                                            <FiCheck size={16} />
                                        </button>
                                    )}
                                    {notification.is_dismissable !== false && (
                                        <button
                                            className="nif-item-btn danger"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dismissNotification(notification.id);
                                            }}
                                            title="Dismiss"
                                        >
                                            <FiX size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NotificationInboxFull;
